import { useState, useMemo } from 'react'
import Header from './components/Header'
import Footer from './components/Footer'
import './styles/App.css'
import {
  CONTEXT_PRESETS,
  CONTEXT_ESTIMATE_DISCLAIMER,
  getTextStats,
  calculateAllContextFits,
  calculateAllContextFitsFromEstimatedOutput,
  formatNumber,
  estimateOutputTokens
} from './utils/tokenizer'

function App() {
  const [inputText, setInputText] = useState('')
  const [outputText, setOutputText] = useState('')
  const [outputRatio, setOutputRatio] = useState(0.5)
  const [useEstimatedOutput, setUseEstimatedOutput] = useState(true)
  const [selectedPresets, setSelectedPresets] = useState<string[]>(['context-128k', 'context-200k'])

  const inputStats = useMemo(() => {
    if (!inputText) return null
    return getTextStats(inputText)
  }, [inputText])

  const outputStats = useMemo(() => {
    if (useEstimatedOutput || !outputText) return null
    return getTextStats(outputText)
  }, [outputText, useEstimatedOutput])

  const estimatedOutputTokenCount = useMemo(
    () => estimateOutputTokens(inputStats?.tokenCount || 0, outputRatio),
    [inputStats?.tokenCount, outputRatio]
  )

  const contextFits = useMemo(() => {
    if (!inputText) return []

    const fits = useEstimatedOutput
      ? calculateAllContextFitsFromEstimatedOutput(inputText, outputRatio)
      : calculateAllContextFits(inputText, outputText)

    return fits
      .filter(e => selectedPresets.length === 0 || selectedPresets.includes(e.presetId))
      .sort((a, b) => a.contextLimit - b.contextLimit)
  }, [inputText, outputText, useEstimatedOutput, outputRatio, selectedPresets])

  const togglePreset = (presetId: string) => {
    setSelectedPresets(prev =>
      prev.includes(presetId)
        ? prev.filter(id => id !== presetId)
        : [...prev, presetId]
    )
  }

  const selectAllPresets = () => {
    setSelectedPresets(Object.keys(CONTEXT_PRESETS))
  }

  const clearAllPresets = () => {
    setSelectedPresets([])
  }

  return (
    <div className="app">
      <Header
        title="Token Counter & Prompt Size Estimator"
        description="Estimate prompt tokens and context-window fit before sending long AI coding prompts"
      />

      <main className="main-content">
        <div className="container">
          {/* Input Text */}
          <div style={{ marginBottom: '1rem' }}>
            <h3>Input Text (Prompt)</h3>
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Paste or type your input text here..."
              style={{
                width: '100%',
                minHeight: '150px',
                padding: '0.75rem',
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
            {inputStats && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                <strong>{formatNumber(inputStats.characterCount)}</strong> characters, {' '}
                <strong>{formatNumber(inputStats.wordCount)}</strong> words, {' '}
                <strong>{formatNumber(inputStats.tokenCount)}</strong> tokens
              </div>
            )}
          </div>

          {/* Output Configuration */}
          <div style={{ marginBottom: '1rem' }}>
            <h3>Output Text (Response)</h3>

            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'block', margin: '0.5rem 0' }}>
                <input
                  type="radio"
                  checked={useEstimatedOutput}
                  onChange={() => setUseEstimatedOutput(true)}
                />
                {' '}Estimate output tokens
              </label>
              {useEstimatedOutput && (
                <div style={{ marginLeft: '1.5rem' }}>
                  <label>
                    Output ratio:
                    <input
                      type="range"
                      min="0.1"
                      max="2"
                      step="0.1"
                      value={outputRatio}
                      onChange={(e) => setOutputRatio(parseFloat(e.target.value))}
                      style={{ marginLeft: '0.5rem', width: '200px' }}
                    />
                    <span style={{ marginLeft: '0.5rem' }}>{outputRatio.toFixed(1)}x</span>
                  </label>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                    Estimated: ~{formatNumber(estimatedOutputTokenCount)} tokens
                  </div>
                </div>
              )}

              <label style={{ display: 'block', margin: '0.5rem 0' }}>
                <input
                  type="radio"
                  checked={!useEstimatedOutput}
                  onChange={() => setUseEstimatedOutput(false)}
                />
                {' '}Enter actual output text
              </label>
            </div>

            {!useEstimatedOutput && (
              <div>
                <textarea
                  value={outputText}
                  onChange={(e) => setOutputText(e.target.value)}
                  placeholder="Paste expected output/response here..."
                  style={{
                    width: '100%',
                    minHeight: '100px',
                    padding: '0.75rem',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
                {outputStats && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                    <strong>{formatNumber(outputStats.tokenCount)}</strong> tokens
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Context Preset Selection */}
          <div style={{ marginBottom: '1rem' }}>
            <h3>Select Context Windows to Compare</h3>
            <div style={{ marginBottom: '0.5rem' }}>
              <button onClick={selectAllPresets} className="btn-secondary" style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}>
                Select All
              </button>
              <button onClick={clearAllPresets} className="btn-secondary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}>
                Clear All
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
              {Object.entries(CONTEXT_PRESETS).map(([id, preset]) => (
                <label key={id} style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem' }}>
                  <input
                    type="checkbox"
                    checked={selectedPresets.includes(id)}
                    onChange={() => togglePreset(id)}
                  />
                  <span style={{ marginLeft: '0.5rem' }}>{preset.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Context Fit Estimates */}
          {contextFits.length > 0 && (
            <div className="context-fit-estimates" style={{ marginBottom: '1rem' }}>
              <h3>Context Fit Estimates</h3>
              <div style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: '#f0f0f0' }}>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Context Window</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'right' }}>Input Tokens</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'right' }}>Output Tokens</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'right' }}>Total Tokens</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'right' }}>Context Used</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'right' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contextFits.map(est => (
                      <tr key={est.presetId} style={{ background: est.withinLimit ? 'white' : '#fee' }}>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          <strong>{est.presetName}</strong>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            {est.description}
                          </div>
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'right' }}>
                          {formatNumber(est.inputTokens)}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'right' }}>
                          {formatNumber(est.outputTokens)}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'right' }}>
                          <strong>{formatNumber(est.totalTokens)}</strong>
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'right' }}>
                          {est.percentageOfLimit.toFixed(1)}%
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            of {formatNumber(est.contextLimit)}
                          </div>
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'right' }}>
                          {est.withinLimit ? 'Fits' : <span style={{ color: 'red' }}>Exceeds</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="privacy-notice">
            <h3>Browser-local text processing</h3>
            <p>
              Token counting and context math run in this browser.
              Your pasted text is not sent to an AI Coding Club API.
            </p>
            <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: '#666' }}>
              {CONTEXT_ESTIMATE_DISCLAIMER}
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default App
